import KeenSlider from "keen-slider";
import { whenDomReady } from "@allwyn/utils/document-ready";
import useKeenAdaptiveHeightPlugin from "@allwyn/utils/keen-slider/plugins/keen-adaptive-height-plugin";
import { ResponsibleGamingAnalytics } from "../responsible-gaming-analytics";

/* eslint-disable @typescript-eslint/naming-convention */
const ELEMENTS = {
    ROOT: "#rg-test",
    QUESTIONS: "#rg-test-questions",
    ANSWERS: "#rg-answers",
    INTRO: "#rg-test-intro",
    QUESTION_NUMBER: "#rg-test-question-number",
    BUTTON_YES: ".js-rg-test-button-yes",
    BUTTON_NO: ".js-rg-test-button-no",
    ANSWER: ".responsible-gaming-test-widget__answer",
} as const;

const CLASSES = {
    INITED: "inited",
    ACTIVE_SLIDE: "responsible-gaming-test-widget__slide--active",
} as const;

function showElement(element: HTMLElement): void {
    element.style.display = "";
}

function showBlockElement(element: HTMLElement, root: HTMLElement): void {
    element.style.display = "block";
    root.style.marginTop = `${element.offsetHeight / 4}px`;
}

function hideElement(element: HTMLElement): void {
    element.style.display = "none";
}

function setQuestionNumber(value: number): void {
    const questionNumber = document.querySelector<HTMLElement>(ELEMENTS.QUESTION_NUMBER);
    if (!questionNumber) {
        return;
    }

    questionNumber.textContent = value < 10 ? `0${value}` : value.toString();
}

function scrollToAnswer(element: HTMLElement): void {
    requestAnimationFrame(() => {
        const answerRect = element.getBoundingClientRect();
        const answerImage = element.querySelector<HTMLElement>(".responsible-gaming-test-widget__answer-image");
        const imageRect = answerImage?.getBoundingClientRect();
        const overlap = imageRect ? Math.max(0, answerRect.top - imageRect.top) : 0;
        const topGap = 96;

        window.scrollTo({
            top: Math.max(0, window.scrollY + answerRect.top - overlap - topGap),
            behavior: "smooth",
        });
    });
}

function initHsrResponsibleGamingWidget(): void {
    const root = document.querySelector<HTMLElement>(ELEMENTS.ROOT);
    const rootQuestions = document.querySelector<HTMLElement>(ELEMENTS.QUESTIONS);
    const rootAnswers = document.querySelector<HTMLElement>(ELEMENTS.ANSWERS);

    if (!root || !rootQuestions || !rootAnswers) {
        return;
    }

    const questionElements = Array.from(rootQuestions.children).filter((item) => item instanceof HTMLElement);

    if (!questionElements.length) {
        return;
    }

    questionElements.forEach((question) => {
        showElement(question);
    });

    hideElement(rootAnswers);

    const responses: boolean[] = [];

    const setActiveSlide = (activeIndex: number): void => {
        questionElements.forEach((questionElement, index) => {
            questionElement.classList.toggle(CLASSES.ACTIVE_SLIDE, index === activeIndex);
        });
    };

    const slider = new KeenSlider(
        rootQuestions,
        {
            drag: false,
            slides: {
                perView: 1,
            },
            created(instance) {
                rootQuestions.classList.add(CLASSES.INITED);
                setActiveSlide(instance.track.details.rel);
            },
            slideChanged(instance) {
                setActiveSlide(instance.track.details.rel);
            },
            updated(instance) {
                setActiveSlide(instance.track.details.rel);
            },
            destroyed() {
                rootQuestions.classList.remove(CLASSES.INITED);
            },
        },
        [useKeenAdaptiveHeightPlugin]
    );

    const saveAndMove = (id: number, value: boolean): void => {
        responses.push(value);

        if (id < questionElements.length - 1) {
            slider.next();
            setQuestionNumber(id + 2);

            if (id === 0) {
                ResponsibleGamingAnalytics.pushStartTestEvent();
            }
            return;
        }

        const testIntro = document.querySelector<HTMLElement>(ELEMENTS.INTRO);
        if (testIntro) {
            hideElement(testIntro);
        }

        const yesCount = responses.filter(Boolean).length;

        showElement(rootAnswers);

        const answerElements = rootAnswers.querySelectorAll<HTMLElement>(ELEMENTS.ANSWER);
        let matchedAnswer: HTMLElement | null = null;

        answerElements.forEach((answerElement) => {
            const from = Number(answerElement.dataset.from);
            const to = Number(answerElement.dataset.to);
            const isInRange = !Number.isNaN(from) && !Number.isNaN(to) && from <= yesCount && yesCount <= to;

            if (isInRange) {
                showBlockElement(answerElement, root);
                if (!matchedAnswer) {
                    matchedAnswer = answerElement;
                }
            } else {
                hideElement(answerElement);
            }
        });

        if (!matchedAnswer && answerElements.length) {
            matchedAnswer = answerElements[0];
            showBlockElement(matchedAnswer, root);
        }

        if (matchedAnswer) {
            scrollToAnswer(matchedAnswer);
        }

        ResponsibleGamingAnalytics.pushFinishTestEvent(yesCount, responses);
    };

    root.querySelectorAll<HTMLElement>(ELEMENTS.BUTTON_YES).forEach((button) => {
        button.addEventListener("click", () => {
            slider.update();
            const id = Number(button.dataset.question);
            if (!Number.isNaN(id)) {
                saveAndMove(id, true);
            }
        });
    });

    root.querySelectorAll<HTMLElement>(ELEMENTS.BUTTON_NO).forEach((button) => {
        button.addEventListener("click", () => {
            const id = Number(button.dataset.question);
            if (!Number.isNaN(id)) {
                saveAndMove(id, false);
            }
        });
    });
}

await whenDomReady();
initHsrResponsibleGamingWidget();
